import { getAuthHeaders } from '../utils/authHeaders';
import { createFormPdf, PDF_COLORS } from '../utils/formPdf';

const API_URL = 'https://app.pvslimited.co.uk/api';

const DECLARATION_ITEMS = [
  'I have read, understood and had the opportunity to ask questions on the contents of the above controlled document (Risk Assessment, Method Statement, Safe System of Work or Policy as applicable).',
  'I have received and understood the appropriate instruction, information and training to carry out the activity safely and competently, including the safe use of any work equipment provided (Reg. 9, PUWER 1998).',
  'I will take reasonable care for the health and safety of myself and of other persons who may be affected by my acts or omissions, in accordance with my duties under Section 7 of the Health & Safety at Work etc. Act 1974.',
  'I will co-operate with my employer so far as is necessary to enable any statutory duty to be performed and will not intentionally or recklessly interfere with or misuse anything provided in the interests of health, safety or welfare (Sections 7(b) & 8 HSWA 1974).',
  'I will use any equipment, safety devices, personal protective equipment (PPE) and materials in accordance with the training, instructions and information I have received (Reg. 14, Management of Health and Safety at Work Regulations 1999; PPER 1992).',
  'I will inform my supervisor or a Director immediately of any work situation, defect, near-miss, accident, incident, dangerous occurrence or shortcoming in protective measures, in line with PVS reporting procedures and RIDDOR 2013 where applicable.',
  'I understand that I must only carry out work covered by this document if I am authorised and competent to do so, and I will stop work and seek clarification before proceeding if any part of the document, hazard or control measure is unclear.',
  'I will work strictly in accordance with the applicable Written Scheme of Examination (WSE), procedures and acceptance criteria, and will not deviate from approved methods without written authorisation.',
  'I will act impartially and objectively, basing all inspection, refurbishment and quality decisions solely on factual evidence and documented requirements, and will declare any actual, potential or perceived conflict of interest to a Director without delay (ISO/IEC 17020).',
  'I will treat all client and company information, inspection outcomes, technical data and personal data as confidential and will only access or disclose such information on a strict need-to-know basis in accordance with PVS confidentiality and data-protection procedures.',
  'I acknowledge that this signed record may be retained by PVS and shared with regulators, accreditation bodies (including UKAS) and clients as evidence of competence, briefing and document control.',
];

const sendAcknowledmentEmail = async (formData) => {
  try {
    const blob = await createFormPdf({
      title: 'EMPLOYEE ACKNOWLEDGMENT OF RISK ASSESSMENT & METHOD STATEMENT (RAMS)',
    }, (pdf) => {
      pdf.section('DOCUMENT DETAILS');
      pdf.field('Document Name', formData.documentName);
      pdf.field('Document Reference', formData.documentReference);
      pdf.field('Process/Activity Covered', formData.processActivity);
      pdf.field('Version Number', formData.versionNumber);
      pdf.field('Date of Issue', formData.dateOfIssue);

      pdf.section('EMPLOYEE DECLARATION');
      pdf.paragraph('By signing below I confirm that:', { size: 10, color: PDF_COLORS.dark });
      DECLARATION_ITEMS.forEach((item) => pdf.bullet(item));
      pdf.paragraph(
        'This declaration supports compliance with the Health & Safety at Work etc. Act 1974, the Management of Health and Safety at Work Regulations 1999, PUWER 1998, PPER 1992, RIDDOR 2013 and the impartiality, confidentiality and competence requirements of ISO/IEC 17020 as assessed by UKAS.',
        { size: 8, color: PDF_COLORS.muted }
      );

      pdf.section('EMPLOYEE DETAILS');
      pdf.field('Name', formData.employeeName);
      pdf.field('Email', formData.employeeEmail);
      pdf.field('Job Role', formData.jobRole);
      pdf.field('Signature', formData.signature);
      pdf.field('Date', formData.employeeDate);

      if (formData.supervisorName) {
        pdf.section('SUPERVISOR/MANAGER VERIFICATION');
        pdf.paragraph(
          'I confirm that the above employee has been briefed on this RAMS and is competent to undertake the activity described.',
          { size: 10, color: PDF_COLORS.dark }
        );
        pdf.field('Supervisor Name', formData.supervisorName);
        pdf.field('Signature', formData.supervisorSignature);
        pdf.field('Date', formData.supervisorDate);
      }
    });

    const formDataToSend = new FormData();
    const safeName = String(formData.employeeName || 'employee').replace(/[^a-zA-Z0-9-_]/g, '_');
    formDataToSend.append('file', blob, `Employee_Acknowledgment_${safeName}_${Date.now()}.pdf`);
    formDataToSend.append('documentName', formData.documentName);
    formDataToSend.append('employeeName', formData.employeeName);
    formDataToSend.append('employeeEmail', formData.employeeEmail);
    formDataToSend.append('jobRole', formData.jobRole);
    formDataToSend.append('signature', formData.signature);
    formDataToSend.append('employeeDate', formData.employeeDate);
    formDataToSend.append('documentReference', formData.documentReference || '');
    formDataToSend.append('processActivity', formData.processActivity || '');
    formDataToSend.append('versionNumber', formData.versionNumber || '');
    formDataToSend.append('dateOfIssue', formData.dateOfIssue || '');
    formDataToSend.append('supervisorName', formData.supervisorName || '');
    formDataToSend.append('supervisorSignature', formData.supervisorSignature || '');
    formDataToSend.append('supervisorDate', formData.supervisorDate || '');

    const response = await fetch(`${API_URL}/send-acknowledgment-with-attachment`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: formDataToSend,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }

    return response.json();
  } catch (error) {
    console.error('Error in sendAcknowledmentEmail:', error);
    throw error;
  }
};

export default sendAcknowledmentEmail;
